import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { HStack, Text, IconButton, VStack, chakra } from '@chakra-ui/react';
import classNames from 'classnames';

import Loader from '../../../../components/Loader';
import { getContentRoute } from './helpers';
import { LessonName, LeftColumn, ToggleSideBar } from './index';
import { FullUnit } from '../../hooks/api/types';
import { User } from '../../../../context/AuthProvider';
import useTutorialInfo, { InfoStep } from '../../../../hooks/useTutorialInfo';
import DotInfo from '../../../../components/DotInfo';
import { FolderTreeIcon } from '../../../../components/icons';
import { ChevronRightIcon, HamburgerIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';

interface SidebarProps {
  studyGroup: any;
  isModuleLoading: boolean;
  unit?: FullUnit;
  chalkups: any[];
  context: User | null;
  isSaveAvailable: boolean;
  handleOpenModal: () => void;
  module?: { pk: string };
  content?: { pk: string };
  getRoute?: typeof getContentRoute;
}

const Sidebar: React.FC<SidebarProps> = (props) => {
  const {
    isModuleLoading,
    unit,
    chalkups,
    context,
    isSaveAvailable,
    handleOpenModal,
    module: selectedModule,
    content: selectedContent,
  } = props;
  let { getRoute } = props;
  const [isSidebarOpened, setIsSidebarOpened] = useState(true);
  const [openedModuleIds, setOpenedModuleIds] = useState<string[]>([]);

  const studyGroupId = useParams<{ studyGroupId: string }>()?.studyGroupId;
  const { t } = useTranslation();

  const toggleModule = (moduleId: string) => {
    if (!openedModuleIds.includes(moduleId)) {
      setOpenedModuleIds([...openedModuleIds, moduleId]);
    } else {
      setOpenedModuleIds([...openedModuleIds.filter((id) => id !== moduleId)]);
    }
  };

  const { currentStep, nextStep } = useTutorialInfo();
  const isModuleInfoShown = currentStep === InfoStep.OPEN_MODULE;
  const isContentInfoShown = currentStep === InfoStep.OPEN_CONTENT;

  if (!getRoute) {
    getRoute = getContentRoute;
  }

  return (
    <div
      style={{
        transition: '0.5s',
        paddingLeft: '32px',
        marginRight: isSidebarOpened ? '0px' : '-24px',
        position: 'relative',
      }}
    >
      <ToggleSideBar
        opened={isSidebarOpened}
        onClick={() => setIsSidebarOpened(!isSidebarOpened)}
        title={
          isSidebarOpened
            ? t('guidesPage.closeSideMenu')
            : t('guidesPage.openSideMenu')
        }
      >
        <IconButton
          zIndex="50"
          position="absolute"
          mt={-6}
          // top="0"
          // mt="4"
          // left={isSidebarOpened ? '286px' : '32px'}
          title={
            isSidebarOpened
              ? t('guidesPage.closeSideMenu')
              : t('guidesPage.openSideMenu')
          }
          aria-label={
            isSidebarOpened
              ? t('guidesPage.closeSideMenu')
              : t('guidesPage.openSideMenu')
          }
          fontSize="20px"
          onClick={() => {
            setIsSidebarOpened(!isSidebarOpened);
          }}
          transition="all 0.5s ease"
          icon={isSidebarOpened ? <HamburgerIcon /> : <FolderTreeIcon />}
        />
      </ToggleSideBar>
      <LeftColumn opened={isSidebarOpened} spacing={0}>
        {unit?.modules?.map((module, moduleIndex) => {
          const activeModule =
            selectedModule?.pk === module.pk ||
            openedModuleIds.includes(module.pk);

          const hasDemoContent = module.contents.find((c) => c.isDemo);

          const showModuleInfo =
            isModuleInfoShown &&
            unit.modules.findIndex((m) => m.contents.find((c) => c.isDemo)) ===
              moduleIndex;

          return (
            <VStack
              w="full"
              className="module"
              id={`module-${module.pk}`}
              key={module.pk}
              spacing={0}
            >
              <chakra.div
                w="full"
                data-number={moduleIndex + 1}
                className={classNames([
                  'module-title',
                  { 'active-module': activeModule },
                  {
                    done: module.contents?.reduce((acc, content) => {
                      if (!acc) {
                        return false;
                      }
                      const isDone = chalkups?.find(
                        (c) => c.content?.pk === content.pk,
                      );
                      return isDone;
                    }, true),
                  },
                ])}
                style={
                  context?.is_demo && !hasDemoContent ? { opacity: 0.5 } : {}
                }
                onClick={() => {
                  toggleModule(module.pk);
                  if (showModuleInfo) {
                    nextStep();
                  }
                }}
              >
                <HStack
                  w="full"
                  spacing={0}
                  justifyContent="space-between"
                  gap={4}
                >
                  <Text as="h5" textStyle="h5">
                    {module.name}
                  </Text>
                  <ChevronRightIcon
                    boxSize="24px"
                    transition="200ms all ease-in-out"
                    transform={
                      activeModule ? 'rotate(90deg)' : 'rotate(-90deg)'
                    }
                  />
                </HStack>
                {showModuleInfo && (
                  <DotInfo
                    style={{ position: 'absolute', right: 0 }}
                    info="Open module"
                  />
                )}
              </chakra.div>
              {activeModule ? (
                <VStack
                  w="full"
                  className="content-title-container"
                  spacing={0}
                >
                  {isModuleLoading && <Loader />}
                  {module.contents?.map((content, contentIndex) => {
                    const activeContent = selectedContent?.pk === content?.pk;
                    const isDone = chalkups?.find(
                      (c) => c.content?.pk === content.pk,
                    );

                    if (!content.isDemo && context?.is_demo) {
                      return (
                        <LessonName key={content.pk} disabled isDone={isDone}>
                          {content.name}
                        </LessonName>
                      );
                    }

                    if (activeContent) {
                      return (
                        <LessonName
                          key={content.pk}
                          active={true}
                          isDone={isDone}
                        >
                          {content.name}
                        </LessonName>
                      );
                    }

                    const showContentInfo =
                      isContentInfoShown &&
                      module.contents.findIndex((c) => c.isDemo) ===
                        contentIndex;

                    return (
                      <Link
                        key={content.pk}
                        onClick={(e) => {
                          if (showContentInfo) {
                            nextStep();
                          }
                          if (isSaveAvailable) {
                            alert('Please save all changes');
                            e.preventDefault();
                            e.stopPropagation();
                            return false;
                          }
                          return true;
                        }}
                        to={getRoute!(studyGroupId, content.pk)}
                      >
                        <LessonName
                          active={false}
                          isDone={isDone}
                          highlight={context?.is_demo && content.isDemo}
                        >
                          {content.name}
                        </LessonName>
                        {showContentInfo && (
                          <DotInfo
                            style={{ position: 'absolute', right: 0, top: 0 }}
                            info="Open content"
                          />
                        )}
                      </Link>
                    );
                  })}
                </VStack>
              ) : null}
            </VStack>
          );
        })}
      </LeftColumn>
    </div>
  );
};

export default Sidebar;
